import { useMemo, useState } from "react";

import type { DailyStat } from "../api";
import { formatMonthDay, formatWeekday } from "../format";

interface BarChartProps {
  days: DailyStat[];
  timezone: string;
}

/**
 * Daily fasting-hours bar chart mirroring the iOS History chart: accent bars,
 * warning-colored when below that day's target, a dashed target rule, and an
 * hours label above each non-empty bar. Hovering a bar shows its date and
 * duration; the x-axis shows sampled date labels.
 */
export function BarChart({ days }: BarChartProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  const model = useMemo(() => {
    const hoursFor = (d: DailyStat) => d.total_seconds / 3600;
    const maxHours = days.reduce((m, d) => Math.max(m, hoursFor(d)), 0);
    const target = days.length > 0 ? (days[days.length - 1] as DailyStat).target_seconds / 3600 : 16;
    const ceiling = Math.max(maxHours, target) * 1.15 || 1;
    // Label roughly 7 evenly-spaced ticks so 30-day axes don't crowd.
    const step = Math.max(1, Math.ceil(days.length / 7));
    const useWeekday = days.length <= 8;
    return { ceiling, target, step, useWeekday };
  }, [days]);

  if (days.length === 0) {
    return <div className="empty">No data for this range.</div>;
  }

  const targetPct = (model.target / model.ceiling) * 100;

  return (
    <div className="chart-card">
      <div className="bars" onMouseLeave={() => setHovered(null)}>
        <div className="target-line" style={{ bottom: `${targetPct}%` }}>
          <span className="target-tag num">{Math.round(model.target)}h</span>
        </div>

        {hovered !== null && days[hovered] && (
          <div
            className="bar-tooltip"
            style={{ left: `${((hovered + 0.5) / days.length) * 100}%` }}
          >
            <strong>{formatMonthDay((days[hovered] as DailyStat).date)}</strong>
            <span className="num">
              {((days[hovered] as DailyStat).total_seconds / 3600).toFixed(1)}h
            </span>
          </div>
        )}

        {days.map((d, i) => {
          const hours = d.total_seconds / 3600;
          const heightPct = Math.max(0, (hours / model.ceiling) * 100);
          const below = d.total_seconds > 0 && d.total_seconds < d.target_seconds;
          const rounded = Math.round(hours);
          return (
            <div
              className="bar-col"
              key={d.date}
              onMouseEnter={() => setHovered(i)}
            >
              {rounded > 0 && (
                <span className={below ? "bar-val num below" : "bar-val num"}>{rounded}</span>
              )}
              <div
                className={
                  (below ? "bar-fill below" : "bar-fill") + (hovered === i ? " hovered" : "")
                }
                style={{ height: `${heightPct}%` }}
              />
            </div>
          );
        })}
      </div>

      <div className="bars-axis">
        {days.map((d, i) => (
          <div className="axis-tick" key={d.date}>
            {i % model.step === 0 ? (model.useWeekday ? formatWeekday(d.date) : formatMonthDay(d.date)) : ""}
          </div>
        ))}
      </div>
    </div>
  );
}
